/**
 * Date-only utilities.
 *
 * IMPORTANT: Never use `new Date('YYYY-MM-DD')` for date-only strings.
 * Browsers interpret it as UTC, which shifts the day for many timezones.
 */

/**
 * Parses a YYYY-MM-DD date-only string into a *local* Date.
 * Uses 12:00 (noon) local time to avoid DST edge cases around midnight.
 */
export const parseYYYYMMDDToLocalDate = (value: string): Date | undefined => {
  const match = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(value);
  if (!match) return undefined;

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);

  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || !Number.isFinite(day)) return undefined;
  return new Date(year, monthIndex, day, 12, 0, 0, 0);
};

export const formatYYYYMMDDToLocaleDateString = (
  value: string,
  locales?: string | string[],
  options?: Intl.DateTimeFormatOptions
): string => {
  const d = parseYYYYMMDDToLocalDate(value);
  return d ? d.toLocaleDateString(locales, options) : value;
};

/**
 * Returns today's date in YYYY-MM-DD using local (browser) time.
 * No UTC, no locale formatting — guaranteed zero-padded.
 */
export const getLocalTodayYMD = (): string => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
